import { GanttTask, Dependency, GanttMilestone, GanttUser, PendingChange } from '../types/gantt.types'
import { SiteInfo } from '../views/CalendarMultiView'
import { useInvalidateSignals } from './useTaskSignals'
import { DirtyHandle, DirtySaveNotice } from '../pages/SchedulerPage'
import { useState, useRef, useCallback, useEffect } from 'react'
import { DateChange, OrderDateWarning, patchScheduleTaskDates, taskDateSaveErrorMessage } from '../api/foremanApi'
import {
  CascadePlan, predecessorWarnings, validateScheduleEndWarn, computeCascadeReschedule, getCascadeIds,
  holidayGateError, countWorkdays, addWorkdays, nextWorkday, type HolidayGate,
} from '../views/scheduleUtils'
import { formatISO, addDays } from '../constants/gantt.logic'
import { EXTRA_PAD_DAYS, LEAD_PAD_DAYS } from '../constants/gantt.const'
import { notifiableDateChanges } from '../components/ScheduleChangeNoticeModal'
import { toast } from 'sonner'
import { computePeriodSyncPlan, type PeriodSyncPlan } from '../constants/periodSync.logic'
import { periodSyncCancelledError } from './usePeriodSyncConfirm'
// 날짜 이동 체계(캐스케이드 적용 · 당기기 확인 · 소요일 · 취소 · 저장 · 미저장 보고) — 이동 전 GanttView.tsx 본문을 그대로 옮긴 것이다.

export type UseGanttDateMoveArgs = {
  tasksRef: React.MutableRefObject<GanttTask[]>
  depsRef: React.MutableRefObject<Dependency[]>
  scheduleEndRef: React.MutableRefObject<Date | null>
  setTasks: React.Dispatch<React.SetStateAction<GanttTask[]>>
  setGanttBounds: React.Dispatch<React.SetStateAction<{ start: Date; end: Date; }>>
  pendingChangesRef: React.MutableRefObject<Map<number, PendingChange>>
  originalTasksRef: React.MutableRefObject<GanttTask[]>
  setPendingChanges: React.Dispatch<React.SetStateAction<Map<number, PendingChange>>>
  holidaysRef: React.MutableRefObject<Set<string>>
  /** 공휴일 조회 상태·범위 거울 — 조회 중·실패·범위 밖이면 날짜 계산·저장을 막는다(A-08) */
  holidayGateRef: React.MutableRefObject<HolidayGate>
  /** 이동 가능 범위(표시 기간 앞 2주·뒤 3일) 거울 — 로드·저장·취소 시점에 정해진다(A-11) */
  movableRangeRef: React.MutableRefObject<{ start: Date; end: Date }>
  milestonesRef: React.MutableRefObject<GanttMilestone[]>
  ganttBoundsRef: React.MutableRefObject<{ start: Date; end: Date; }>
  selectedSite: SiteInfo | undefined
  pendingChanges: Map<number, PendingChange>
  user: GanttUser
  invalidateSignals: ReturnType<typeof useInvalidateSignals>
  onDirtyChange: ((dirty: DirtyHandle | null) => void) | undefined
  panelDirty: boolean
  setSelectedPanelTaskId: React.Dispatch<React.SetStateAction<number | null>>
  /** 공종 변경이 스케줄 기간을 넘을 때 '함께 바뀝니다' 확인(true=진행) — usePeriodSyncConfirm */
  confirmPeriodSync: (plan: PeriodSyncPlan) => Promise<boolean>
  /** 스케줄 기간·마커가 함께 바뀐 저장 뒤 — 현장 정보(종료일·D-day)와 간트 데이터 재조회 */
  onPeriodSynced?: () => void
}

// 저장 응답의 동반 이동 경고 → 안내 문구. 화면에 노출하는 것은 2종뿐(이미 발송·입고 완료, 계획 3장 판정)이고 나머지 코드는 안내하지 않는다.
const ORDER_WARNING_TEXT: Record<string, string> = {
  ORDER_ALREADY_SENT: '이미 발주 정보를 보낸 공종이 있어요 · 다시 보내기가 필요해요',
  DELIVERY_RECEIVED: '입고가 끝난 공종은 발주일·입고일을 옮기지 않았어요',
}
function orderWarningText(warnings: OrderDateWarning[]): string | null {
  const lines = Object.keys(ORDER_WARNING_TEXT).filter(code => warnings.some(w => w.code === code)).map(code => ORDER_WARNING_TEXT[code])
  return lines.length > 0 ? lines.join(' / ') : null
}

export function useGanttDateMove({
  tasksRef, depsRef, scheduleEndRef, setTasks, setGanttBounds, pendingChangesRef, originalTasksRef,
  setPendingChanges, holidaysRef, holidayGateRef, movableRangeRef, milestonesRef, ganttBoundsRef, selectedSite,
  pendingChanges, user, invalidateSignals, onDirtyChange, panelDirty, setSelectedPanelTaskId,
  confirmPeriodSync, onPeriodSynced,
}: UseGanttDateMoveArgs) {
  const [saving,         setSaving]         = useState(false)
  // 날짜 저장 직후 변경 카톡 확인 창 — 저장 응답 dateChanges 중 notifiable 건이 있을 때만 채운다.
  const [changeNotice,   setChangeNotice]   = useState<{ scheduleId: number; changes: DateChange[] } | null>(null)
  const [dragError,     setDragError]      = useState('')
  // 저장 응답의 발주·입고 동반 이동 경고(이미 발송·입고 완료) 안내 — 닫기 전까지 유지
  const [orderNotice,    setOrderNotice]    = useState<string | null>(null)

  // 당기기(시작일을 앞으로)·밀기(뒤로) 모두, 후행공정이 있으면 함께 이동할지 확인하는 다이얼로그
  const [pullConfirm, setPullConfirm] = useState<{
    taskId:    number
    taskName:  string
    newStart:  Date
    newEnd:    Date
    direction: 'pull' | 'push'
  } | null>(null)

  const dragErrorTimer     = useRef<ReturnType<typeof setTimeout> | null>(null)

  // 오류줄 표시(3.5초) — ref·setter 만 쓴다(첫 렌더 함수를 붙드는 리스너가 있으므로 state 값을 읽지 않는다)
  function showDragError(msg: string) {
    if (dragErrorTimer.current) clearTimeout(dragErrorTimer.current)
    setDragError(msg)
    dragErrorTimer.current = setTimeout(() => setDragError(''), 3500)
  }

  // 이동 가능 범위 재설정 — 현재 표시 기간(bounds) 앞 2주·뒤 3일(A-11)
  function resetMovableRange(bounds: { start: Date; end: Date }) {
    movableRangeRef.current = { start: addDays(bounds.start, -LEAD_PAD_DAYS), end: addDays(bounds.end, EXTRA_PAD_DAYS) }
  }

  // 이 공종과 그 후행 전체의 날짜 — 공휴일 조회 범위 확인용
  function cascadeDates(taskIds: number[]): Date[] {
    const ids = new Set<number>()
    for (const id of taskIds) for (const c of getCascadeIds(id, depsRef.current)) ids.add(c)
    const out: Date[] = []
    for (const t of tasksRef.current) if (ids.has(t.taskId)) out.push(t.startDate, t.endDate)
    return out
  }

  // ── 캐스케이드 계획 적용(검증 → 커밋) ──────────────────────────
  // 공휴일 조회 상태·범위(A-08) → 표시 기간 밖 이동(A-11)은 막고(적용 안 함), 선행 종료일 이전 시작(A-14)·공사 기간 초과는
  // 변경을 막지 않고 알림만 표시한 뒤 그대로 적용한다(scheduleUtils.ts 참고). 적용했으면 true.
  // opts.syncOrderDates — 기간 수정 팝오버에서만 지정(true=발주·입고 동반 이동 표시, false=표시 해제). 막대 끌기 등은 지정하지 않아 기존 표시를 유지한다.
  function applyCascadePlan(plan: CascadePlan, opts?: { syncOrderDates?: boolean }): boolean {
    const currentTasks = tasksRef.current

    // 실제로 날짜가 바뀌는 공종만 — 바뀌지 않는 공종(연쇄 안의 그대로 둔 후행)은 검증 대상에서 뺀다
    const changedPlan: CascadePlan = new Map()
    for (const [id, v] of plan) {
      const cur = currentTasks.find(t => t.taskId === id)
      if (!cur || cur.startDate.getTime() !== v.startDate.getTime() || cur.endDate.getTime() !== v.endDate.getTime()) changedPlan.set(id, v)
    }

    const planDates: Date[] = []
    for (const v of changedPlan.values()) planDates.push(v.startDate, v.endDate)
    const gateErr = holidayGateError(holidayGateRef.current, ...planDates)
    if (gateErr) { showDragError(gateErr); return false }

    const mr = movableRangeRef.current
    for (const v of changedPlan.values()) {
      if (v.startDate.getTime() < mr.start.getTime() || v.endDate.getTime() > mr.end.getTime()) {
        showDragError('표시된 공사 기간 밖으로는 옮길 수 없어요')
        return false
      }
    }

    const predWarns = predecessorWarnings(changedPlan, currentTasks, depsRef.current, holidaysRef.current)
    const predMsg = predWarns.length > 0
      ? predWarns[0].message + (predWarns.length > 1 ? ` (외 ${predWarns.length - 1}건)` : '')
      : null
    const warnErr = predMsg ?? validateScheduleEndWarn(plan, currentTasks, scheduleEndRef.current)
    if (warnErr) showDragError(warnErr)

    const updatedTasks = currentTasks.map(t => {
      const p = plan.get(t.taskId)
      return p ? { ...t, startDate: p.startDate, endDate: p.endDate } : t
    })
    setTasks(updatedTasks)

    // pending 업데이트 (원래 날짜와 다른 경우만 추가)
    const newPending = new Map(pendingChangesRef.current)
    for (const id of plan.keys()) {
      const orig    = originalTasksRef.current.find(o => o.taskId === id)
      const updated = updatedTasks.find(u => u.taskId === id)
      if (updated) {
        if (orig && orig.startDate.getTime() === updated.startDate.getTime() && orig.endDate.getTime() === updated.endDate.getTime()) {
          newPending.delete(id)
        } else {
          const sync = opts?.syncOrderDates !== undefined ? opts.syncOrderDates : newPending.get(id)?.syncOrderDates
          newPending.set(id, {
            startDate: formatISO(updated.startDate),
            endDate:   formatISO(updated.endDate),
            ...(sync ? { syncOrderDates: true } : {}),
          })
        }
      }
    }
    setPendingChanges(newPending)
    return true
  }

  // 당기기 확인 다이얼로그 응답 처리 — 'self'는 드래그한 공종만, 'cascade'는 후행공정도
  // "선행 종료일 + 선행 공정의 leadTime(대기일)" 기준으로 다시 계산해서 함께 이동한다.
  function resolvePullConfirm(mode: 'self' | 'cascade') {
    const pc = pullConfirm
    if (!pc) return
    setPullConfirm(null)

    if (mode === 'self') {
      applyCascadePlan(new Map([[pc.taskId, { startDate: pc.newStart, endDate: pc.newEnd }]]))
      return
    }

    const gateErr = holidayGateError(holidayGateRef.current, pc.newStart, pc.newEnd, ...cascadeDates([pc.taskId]))
    if (gateErr) { showDragError(gateErr); return }
    const plan = computeCascadeReschedule(
      pc.taskId, pc.newStart, pc.newEnd, tasksRef.current, depsRef.current, holidaysRef.current, 'always',
    )
    applyCascadePlan(plan)
  }

  // ── 소요일 ▲/▼ 수정 ────────────────────────────────────────
  // 소요일은 작업일 수(A-09) — ▲▼ 한 번에 작업일 1일. 종료일 = 시작일 포함 새 소요일째 작업일.
  // 소요일이 바뀌면(= 종료일이 늘거나 줄면) 후행 공종도 같이 밀리거나 당겨진다.
  // 후행 공종의 새 시작일이 공휴일·일요일에 걸리면 다음 근무일로 넘긴다(작업일 수 유지).
  // 기준 공종의 시작일이 비작업일이면 다음 작업일로 보정한다(A-10, BE 가 비작업일 시작을 거부).
  // 종료일이 늘어나는 쪽(밀기)만 여유일 침범 여부에 따라 후행공정을 같이 밀 수 있고, 줄어드는 쪽은
  // 후행공정의 여유가 늘어날 뿐이라 push-if-violated가 자연히 후행공정을 그대로 둔다.
  //
  // 동일 공종(workTypeCode)이 한 행에 바 여러 개로 모여 있으면, 좌측 소요일 스피너 하나가
  // 그 행의 모든 바(taskIds)에 동시에 적용된다 — 바 하나뿐인 일반적인 행은 taskIds.length===1로
  // 예전 handleDurationChange와 동일하게 동작한다. 순서대로 처리하되 직전 바의 캐스케이드
  // 결과를 반영한 workingTasks를 다음 바 계산에 넘기고, 마지막에 한 번만 커밋한다(경고 메시지
  // 중복 방지, 렌더 1회).
  const handleRowDurationChange = useCallback((taskIds: number[], delta: number) => {
    const preErr = holidayGateError(holidayGateRef.current, ...cascadeDates(taskIds))
    if (preErr) { showDragError(preErr); return }
    let workingTasks = tasksRef.current
    const currentDeps = depsRef.current
    const holidays = holidaysRef.current
    const mergedPlan: CascadePlan = new Map()
    for (const taskId of taskIds) {
      const task = workingTasks.find(t => t.taskId === taskId)
      if (!task) continue
      const duration = Math.max(1, countWorkdays(task.startDate, task.endDate, holidays))
      if (delta < 0 && duration <= 1) continue
      const newStart = nextWorkday(task.startDate, holidays)
      const newEnd = addWorkdays(newStart, duration + delta, holidays)
      const endErr = holidayGateError(holidayGateRef.current, newStart, newEnd)
      if (endErr) { showDragError(endErr); return }
      const plan = computeCascadeReschedule(taskId, newStart, newEnd, workingTasks, currentDeps, holidays, 'push-if-violated')
      for (const [id, v] of plan) mergedPlan.set(id, v)
      workingTasks = workingTasks.map(t => { const p = plan.get(t.taskId); return p ? { ...t, ...p } : t })
    }
    if (mergedPlan.size > 0) applyCascadePlan(mergedPlan)
  }, [])

  // ── 취소 ─────────────────────────────────────────────────────
  const handleCancel = useCallback(() => {
    const orig = originalTasksRef.current
    setTasks(orig)
    setPendingChanges(new Map())
    if (orig.length > 0) {
      const starts   = orig.map(t => t.startDate.getTime())
      const ends     = orig.map(t => t.endDate.getTime())
      const msTimes  = milestonesRef.current.map(m => new Date(m.milestoneDate).getTime())
      const newBounds = {
        start: new Date(Math.min(...starts, ...msTimes)),
        end:   new Date(Math.max(...ends,   ...msTimes)),
      }
      ganttBoundsRef.current = newBounds
      resetMovableRange(newBounds)
      setGanttBounds(newBounds)
    }
  }, [])

  // ── 저장 ─────────────────────────────────────────────────────
  // 저장하고 변경 카톡 확인 대상을 돌려준다. 화면 이탈과 겹치는 "저장하고 이동"은 이 값을 부모(SchedulerPage)가
  // 받아 이동 뒤에도 확인창을 띄운다(FR-C-120). 화면의 [저장] 버튼은 아래 handleSave 가 이 화면 안에서 띄운다.
  const saveDates = useCallback(async (): Promise<DirtySaveNotice | null> => {
    if (!selectedSite?.scheduleId || pendingChanges.size === 0) return null
    // 공휴일 조회가 끝나지 않았거나 실패했으면 저장하지 않는다(A-08) — 변경 내용은 그대로 유지
    const gateErr = holidayGateError(holidayGateRef.current)
    if (gateErr) {
      toast.error(gateErr.replace('날짜를 옮길 수 없어요', '저장할 수 없어요'))
      throw new Error(gateErr)
    }
    // 공종 날짜가 스케줄 시작·종료일을 넘으면 저장 전에 한 번만 묻는다(결정 4·5). 취소하면 저장하지 않고 변경 내용은 유지.
    const periodPlan = computePeriodSyncPlan(selectedSite, [...pendingChanges.entries()].flatMap(([taskId, dates]) => {
      const t = tasksRef.current.find(x => x.taskId === taskId)
      return t ? [{ workTypeCode: t.workTypeCode, workTypeName: t.workTypeName, startDate: dates.startDate, endDate: dates.endDate }] : []
    }))
    if (periodPlan && !(await confirmPeriodSync(periodPlan))) throw periodSyncCancelledError()
    setSaving(true)
    try {
      const headers = user?.compId ? { 'X-Comp-Id': String(user.compId) } : {}
      // 선택 공종 + 후행 이동 공종 전부가 pendingChanges 에 들어 있어 한 번에 저장된다(applyCascadePlan).
      // 기간 수정 팝오버에서 '발주·입고 동반 이동'을 켠 공종은 syncOrderDates 를 실어 보낸다(A-16).
      const { dateChanges, orderChanges, orderWarnings } = await patchScheduleTaskDates(
        selectedSite.scheduleId,
        [...pendingChanges.entries()].map(([taskId, dates]) => ({
          taskId,
          startDate: dates.startDate,
          endDate:   dates.endDate,
          ...(dates.syncOrderDates ? { syncOrderDates: true } : {}),
        })),
        headers,
        periodPlan != null,
      )
      void invalidateSignals()
      if (periodPlan) onPeriodSynced?.()
      // 동반 이동된 발주일·입고일을 간트 마커에 반영
      let savedTasks = tasksRef.current
      if (orderChanges.length > 0) {
        const byTask = new Map(orderChanges.map(c => [c.taskId, c]))
        savedTasks = savedTasks.map(t => {
          const c = byTask.get(t.taskId)
          return c ? { ...t, orderDate: c.newOrderDate, deliveryDate: c.newDeliveryDate } : t
        })
        setTasks(savedTasks)
      }
      originalTasksRef.current = savedTasks
      setPendingChanges(new Map())
      resetMovableRange(ganttBoundsRef.current)
      setOrderNotice(orderWarningText(orderWarnings))
      const notifiable = notifiableDateChanges(dateChanges)
      return notifiable.length > 0 ? { scheduleId: selectedSite.scheduleId, changes: notifiable } : null
    } catch (e) {
      toast.error(taskDateSaveErrorMessage(e))
      throw e // 호출자(저장하고 이동)가 실패를 알 수 있게 다시 던진다(DF-50)
    } finally {
      setSaving(false)
    }
  }, [selectedSite?.scheduleId, selectedSite?.startDate, selectedSite?.endDate, pendingChanges, user?.compId, invalidateSignals, confirmPeriodSync, onPeriodSynced])

  const handleSave = useCallback(async () => {
    try {
      const notice = await saveDates()
      if (notice) setChangeNotice(notice)
    } catch { /* 저장 실패는 saveDates 가 이미 알림 — 변경 내용 유지 */ }
  }, [saveDates])

  // 저장 안 한 날짜 변경사항을 부모(SchedulerPage)에 보고 — 다른 현장 클릭·다른 페이지
  // 이동 시 저장 확인창을 띄우는 데 쓰인다. 화면을 떠날 때(unmount)는 반드시 비운다.
  useEffect(() => {
    const datesDirty = pendingChanges.size > 0
    onDirtyChange?.(datesDirty || panelDirty ? {
      count: pendingChanges.size,
      panel: panelDirty,
      save: saveDates,
      discard: () => { if (datesDirty) handleCancel(); if (panelDirty) setSelectedPanelTaskId(null) },
    } : null)
  }, [pendingChanges, panelDirty, onDirtyChange, saveDates, handleCancel])
  useEffect(() => () => onDirtyChange?.(null), [onDirtyChange])

  return {
    dragErrorTimer, setDragError, setPullConfirm, applyCascadePlan, dragError, handleCancel, saving,
    handleSave, handleRowDurationChange, pullConfirm, resolvePullConfirm, changeNotice, setChangeNotice,
    orderNotice, setOrderNotice,
  }
}
